/**
 * The desk table: one row per site, one column per audit.
 *
 * The audit columns sit next to each other on purpose. Reading across a row
 * answers the question the desk was built for - "how thoroughly have we looked
 * at this site" - and a gap in any column is the row worth acting on.
 *
 * The columns are generated from `AUDIT_KINDS` rather than written out. They
 * were written out while there were two, and adding a third meant editing a
 * track string, a labels interface, two icon imports, a header and a body in
 * lockstep. Now a fourth audit needs a row in `auditKinds.ts` and two strings.
 */
import { ShieldCheck } from "lucide-react";
import { EmptyState, fmtDate, listCard } from "../../ui";
import { AUDIT_KINDS, type AuditDeskRow, type AuditKind } from "../../../lib/siteAudits";
import { AUDIT_KIND_META } from "./auditKinds";
import { AuditScore } from "./AuditScore";

/**
 * Track widths.
 *
 * The audit columns are equal and wider than the rest, because they hold the
 * only thing anyone came here to compare. Built from `AUDIT_KINDS` so the count
 * cannot drift from the number of columns actually rendered. `listCard` derives
 * the card's horizontal-scroll floor from this string, so a new column gives
 * itself room without a second number needing to change somewhere else.
 */
const COLS = [
  "minmax(180px, 1.6fr)",
  ...AUDIT_KINDS.map(() => "minmax(200px, 1.3fr)"),
  "minmax(150px, 1fr)",
  "110px",
].join(" ");

export interface AuditsTableLabels {
  site: string;
  /** Column header per audit, keyed by kind. See `auditKinds.ts`. */
  kinds: Record<AuditKind, string>;
  contact: string;
  lastSeen: string;
  notAudited: string;
  issues: string;
  runs: string;
  openReport: string;
  emptyTitle: string;
  emptyBody: string;
  noContact: string;
}

export function AuditsTable({
  rows,
  loading,
  labels,
  onOpen,
}: {
  rows: AuditDeskRow[];
  loading: boolean;
  labels: AuditsTableLabels;
  onOpen: (domain: string) => void;
}) {
  if (loading && rows.length === 0) {
    return <div className="crm-card flex justify-center py-16 text-muted-foreground">...</div>;
  }

  if (rows.length === 0) {
    return (
      <div className="crm-card">
        <EmptyState icon={ShieldCheck} title={labels.emptyTitle} body={labels.emptyBody} />
      </div>
    );
  }

  return (
    <div className="crm-card" style={listCard(COLS)}>
      <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
        <span>{labels.site}</span>
        {AUDIT_KINDS.map((kind) => {
          const Icon = AUDIT_KIND_META[kind].icon;
          return (
            <span key={kind} className="inline-flex items-center gap-1.5">
              <Icon className="size-[14px]" strokeWidth={1.8} />
              {labels.kinds[kind]}
            </span>
          );
        })}
        <span>{labels.contact}</span>
        <span>{labels.lastSeen}</span>
      </div>

      <div className="crm-rows pt-0">
        {rows.map((row) => {
          // The address is on whichever audit collected it. A visitor who
          // unlocked one audit and not the others still gave us one address, and
          // the desk must not show an empty contact cell for it. Walked in
          // AUDIT_KINDS order so the answer is stable rather than depending on
          // which audit happened to be listed first in a hand-written chain.
          const email = AUDIT_KINDS.map((k) => row.audits[k]?.email).find(Boolean) ?? null;
          return (
            <button
              key={row.domain}
              type="button"
              onClick={() => onOpen(row.domain)}
              className="crm-list-row relative text-left"
              style={{ gridTemplateColumns: COLS }}
            >
              <span className="crm-trunc text-[13px] font-semibold">{row.domain}</span>

              {AUDIT_KINDS.map((kind) => (
                <AuditScore
                  key={kind}
                  cell={row.audits[kind]}
                  emptyLabel={labels.notAudited}
                  issuesLabel={labels.issues}
                  runsLabel={labels.runs}
                  reportLabel={labels.openReport}
                />
              ))}

              {email ? (
                <span className="crm-trunc text-[12.5px]">{email}</span>
              ) : (
                <span className="crm-mut text-[12.5px]">{labels.noContact}</span>
              )}

              <span className="crm-mut crm-tnum text-[12.5px]">{fmtDate(row.lastAt)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
